/**
 * Configuration globale d'Axios avec refresh token automatique
 * et gestion optimisee des erreurs d'annulation
 */

import axios from 'axios';
import { toast } from 'sonner';
import tokenStore from '@/utils/tokenStore';
import apiCache from '@/utils/apiCache';

import { getBackendUrl, getApiUrl } from '@/utils/apiUrl';

// Use centralized URL resolution
const BACKEND_URL = getBackendUrl();
export const API_BASE_URL = getApiUrl();

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const createAxiosInstance = () => {
  const instance = axios.create({
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  // Request interceptor - check cache first for GET requests
  instance.interceptors.request.use(
    (config) => {
      const token = tokenStore.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // FormData uploads: let the browser set multipart/form-data with boundary
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }
      // Check cache for GET requests
      if (config.method === 'get' && !config._skipCache) {
        const cacheKey = config.url + (config.params ? JSON.stringify(config.params) : '');
        const cached = apiCache.get(cacheKey);
        if (cached) {
          config._cached = true;
          config._cachedData = cached;
          // Return cached data by using an adapter that resolves immediately
          config.adapter = () => Promise.resolve({ data: cached, status: 200, statusText: 'OK', headers: {}, config });
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - cache successful GET responses + handle errors
  instance.interceptors.response.use(
    (response) => {
      // Cache successful GET responses
      if (response.config.method === 'get' && !response.config._cached && response.status === 200) {
        const cacheKey = response.config.url + (response.config.params ? JSON.stringify(response.config.params) : '');
        apiCache.set(cacheKey, response.data);
      }
      // Invalidate cache on mutations
      if (['post', 'put', 'delete', 'patch'].includes(response.config.method)) {
        apiCache.clear();
        if (response.data && response.data.approval_pending === true) {
          toast.warning("Message mis en attente d'approbation du super administrateur", { description: 'Il sera envoyé après validation dans la file « Approbations ».', duration: 7000 });
        }
      }
      return response;
    },
    async (error) => {
      // 1. Cancelled requests - SILENT, no toast, no logging
      if (
        error.name === 'AbortError' ||
        error.name === 'CanceledError' ||
        error.code === 'ERR_CANCELED' ||
        axios.isCancel(error)
      ) {
        return Promise.reject({ cancelled: true, original: error });
      }

      const originalRequest = error.config;

      // 2. Auto refresh on 401 (token expired)
      if (error.response?.status === 401 && !originalRequest._retry) {
        const refreshToken = tokenStore.getRefreshToken();

        // Skip refresh for auth endpoints themselves to prevent loops
        const isAuthEndpoint = originalRequest?.url?.includes('/auth/refresh') || originalRequest?.url?.includes('/auth/login');
        if (!refreshToken || isAuthEndpoint) {
          // For auth endpoints, pass through the original error detail
          const detail = error.response?.data?.detail || 'Non authentifie';
          return Promise.reject({ type: 'auth', status: 401, message: detail, original: error });
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const newToken = data.access_token;
          tokenStore.setToken(newToken);
          if (data.refresh_token) {
            tokenStore.setRefreshToken(data.refresh_token);
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Only clear tokens if the refresh endpoint explicitly returned 401
          if (refreshError?.response?.status === 401) {
            tokenStore.clear();
            window.dispatchEvent(new CustomEvent('auth:logout'));
          }
          return Promise.reject({ type: 'auth', status: 401, message: 'Session expiree', original: refreshError });
        } finally {
          isRefreshing = false;
        }
      }

      // 3. Network error — silencieux pour les GET (polling/navigation), toast pour les actions utilisateur
      if (!error.response) {
        const method = (originalRequest?.method || 'get').toLowerCase();
        const isLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        if (method !== 'get' && !isLoginPage) {
          const now = Date.now();
          if (!instance._lastNetworkError || now - instance._lastNetworkError > 30000) {
            instance._lastNetworkError = now;
            toast.error(offline ? 'Vous êtes hors ligne — vérifiez votre connexion internet' : 'Erreur de connexion au serveur');
          }
        }
        return Promise.reject({ type: 'network', message: 'Impossible de se connecter au serveur', original: error });
      }

      // 4. Timeout — silencieux pour les GET, toast pour les actions utilisateur
      if (error.code === 'ECONNABORTED') {
        const method = (originalRequest?.method || 'get').toLowerCase();
        if (method !== 'get') {
          toast.error('La requete a pris trop de temps');
        }
        return Promise.reject({ type: 'timeout', message: 'Delai depasse', original: error });
      }

      // 5. HTTP errors
      const status = error.response?.status;
      const data = error.response?.data;
      const rawDetail = typeof data === 'string' ? data : (data?.detail ?? data?.message);
      const errorMessage = Array.isArray(rawDetail)
        ? rawDetail.map(d => (typeof d === 'string' ? d : d?.msg || 'Donnée invalide')).join(' · ')
        : (rawDetail && typeof rawDetail === 'object') ? 'Requête invalide'
          : rawDetail || error.message || 'Une erreur est survenue';

      switch (status) {
        case 401:
          // Already handled above with refresh
          break;
        case 403:
          toast.error('Vous n\'avez pas les permissions necessaires');
          break;
        case 404:
          // Suppress 404 toasts for data-fetching endpoints to avoid noise during navigation
          break;
        case 422:
          toast.error(errorMessage);
          break;
        case 500: case 502: case 503:
          toast.error('Erreur serveur, veuillez reessayer');
          break;
        default:
          if (status !== 401) {
            toast.error(errorMessage);
          }
      }

      return Promise.reject({ type: 'http', status, message: errorMessage, data, original: error });
    }
  );

  return instance;
};

export const axiosInstance = createAxiosInstance();
export default axiosInstance;

export const createAbortableRequest = (requestFn) => {
  return () => {
    const abortController = new AbortController();
    const request = requestFn(abortController.signal).catch((error) => {
      if (error?.cancelled) return null;
      throw error;
    });
    return { request, abort: () => abortController.abort(), signal: abortController.signal };
  };
};

export const safeRequest = async (requestFn, onSuccess, onError, isMountedRef = { current: true }) => {
  try {
    const response = await requestFn();
    if (isMountedRef.current && onSuccess) onSuccess(response.data);
    return response.data;
  } catch (error) {
    if (error?.cancelled) return null;
    if (isMountedRef.current && onError) onError(error);
    throw error;
  }
};

export const createRequestConfig = (token, signal, additionalConfig = {}) => ({
  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  signal,
  timeout: 15000,
  ...additionalConfig,
});
